import { useState, useEffect, useCallback } from 'react'
import { isPathInWhitelist } from './utils.js'
import type { RegionConfig } from './type.js'

export interface UseRedirectBannerOptions {
  /**
   * 地理位置信息
   */
  geo?: {
    country?: {
      code?: string
    }
  }

  /**
   * 地区配置列表
   */
  regions: RegionConfig[]

  /**
   * 国家到地区的映射
   */
  countryToRegionMap: Record<string, string>

  /**
   * 当前站点的地区代码
   */
  currentRegionCode: string

  /**
   * 当前路径
   */
  pathname: string

  /**
   * 白名单路径（在白名单中不显示横幅）
   */
  excludePaths?: string[]

  /**
   * 保存地区选择的函数
   */
  saveRegion: (regionCode: string, languageCode?: string) => void

  /**
   * 打开地区选择弹窗的函数
   */
  setModalOpen: (open: boolean) => void
}

export interface UseRedirectBannerReturn {
  /**
   * 横幅是否可见
   */
  bannerVisible: boolean

  /**
   * 设置横幅可见性
   */
  setBannerVisible: (visible: boolean) => void

  /**
   * 推荐的地区信息
   */
  recommendedRegion: (RegionConfig & { userCountry?: string }) | null

  /**
   * 用户是否手动关闭了横幅
   */
  userDismissedBanner: boolean

  /**
   * 设置用户已关闭横幅的状态
   */
  setUserDismissedBanner: (dismissed: boolean) => void

  /**
   * "留在这里"按钮处理函数
   */
  handleStayHere: () => void

  /**
   * "切换站点"按钮处理函数
   */
  handleSwitchSite: () => void
}

/**
 * 地区重定向横幅控制 Hook
 *
 * 自动检测用户是否被重定向，并显示提示横幅
 * 提供"留在这里"和"切换站点"的交互
 *
 * @example
 * ```tsx
 * const {
 *   bannerVisible,
 *   setBannerVisible,
 *   recommendedRegion,
 *   handleStayHere,
 *   handleSwitchSite,
 * } = useRedirectBanner({
 *   geo,
 *   regions,
 *   countryToRegionMap,
 *   currentRegionCode,
 *   pathname,
 *   excludePaths,
 *   saveRegion,
 *   setModalOpen,
 * })
 *
 * return (
 *   <RegionBanner
 *     visible={bannerVisible}
 *     onVisibleChange={setBannerVisible}
 *     onStayHere={handleStayHere}
 *     onSwitchSite={handleSwitchSite}
 *     recommendedRegion={recommendedRegion}
 *   />
 * )
 * ```
 */
export function useRedirectBanner(options: UseRedirectBannerOptions): UseRedirectBannerReturn {
  const {
    geo,
    regions,
    countryToRegionMap,
    currentRegionCode,
    pathname,
    excludePaths = [],
    saveRegion,
    setModalOpen,
  } = options

  const [bannerVisible, setBannerVisible] = useState(false)
  const [recommendedRegion, setRecommendedRegion] = useState<(RegionConfig & { userCountry?: string }) | null>(null)
  const [userDismissedBanner, setUserDismissedBanner] = useState(false)

  // 检查是否是自动跳转过来的，如果是则显示横幅
  useEffect(() => {
    if (typeof window === 'undefined') return

    const autoRedirected = sessionStorage.getItem('auto_redirected')

    if (autoRedirected === 'true' && geo?.country?.code) {
      // 检查当前路径是否在白名单中
      if (isPathInWhitelist(pathname, excludePaths)) {
        // 在白名单中，清除标记但不显示横幅
        sessionStorage.removeItem('auto_redirected')
        sessionStorage.removeItem('auto_redirect_from')
        return
      }

      // 清除标记（只显示一次）
      sessionStorage.removeItem('auto_redirected')
      sessionStorage.removeItem('auto_redirect_from')

      // 设置推荐的地区信息
      const userCountry = geo.country.code.toUpperCase()
      const recommendedRegionCode = countryToRegionMap[userCountry]

      // 只有在用户的国家有映射配置时才显示横幅
      if (recommendedRegionCode) {
        const targetRegion = regions.find(r => r.code === recommendedRegionCode)
        if (targetRegion) {
          setRecommendedRegion({
            ...targetRegion,
            userCountry,
          })
          // 显示横幅
          setBannerVisible(true)
        }
      }
    }
  }, [geo, countryToRegionMap, regions, pathname, excludePaths])

  // "留在这里"按钮处理
  const handleStayHere = useCallback(() => {
    // 查找当前地区的语言代码
    const currentRegion = regions.find(r => r.code === currentRegionCode)

    // 保存用户选择到 cookie（包含语言代码）
    saveRegion(currentRegionCode, currentRegion?.languageCode)

    // 清除 sessionStorage 标记（如果有）
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('auto_redirected')
      sessionStorage.removeItem('auto_redirect_from')
    }

    // 标记用户已手动关闭横幅
    setUserDismissedBanner(true)

    // 隐藏横幅
    setBannerVisible(false)
  }, [saveRegion, currentRegionCode, regions])

  // "切换站点"按钮处理
  const handleSwitchSite = useCallback(() => {
    // 打开弹窗让用户选择地区
    setModalOpen(true)
  }, [setModalOpen])

  return {
    bannerVisible,
    setBannerVisible,
    recommendedRegion,
    userDismissedBanner,
    setUserDismissedBanner,
    handleStayHere,
    handleSwitchSite,
  }
}
